/*
 * -----------------------------------------------------------------------
 *  <copyright file="uam.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { gql } from '@apollo/client';
import {
    OrganisationAndUserFragment,
    OrganisationAndStudyMemberFragment,
    OrganisationFragment,
    OrganisationMemberFragment,
    OrganisationRoleFragment,
    UserAdminFragment,
    OrganisationFragmentForProductProgram,
    UserFragment,
    SystemUserFragment, OpraPluginFragment
} from './fragments';

export const SAVE_ORGANISATION_ROLE = gql`
    mutation saveOrganisationRole($organisationRole: OrganisationRoleInput!, $reason: String) {
        saveOrganisationRole(organisationRole: $organisationRole, reason: $reason) {
            ...OrganisationRoleFields
        }
    }
    ${OrganisationRoleFragment}
`;

export const GET_ORGANISATION = gql`
    query getOrganisation($key: ObjectId) {
        organisation(key: $key) {
            ...OrganisationFields
        }
    }
    ${OrganisationFragment}
`;

export const GET_ORGANISATION_PROGRAM_PRODUCT = gql`
    query getOrganisationProgramProduct($key: ObjectId) {
        organisation(key: $key) {
            ...OrganisationFieldsForProductProgram
        }
    }
    ${OrganisationFragmentForProductProgram}
`;

export const GET_ORGANISATION_AND_STUDY_MEMBERS = gql`
    query getOrganisationAndStudyMembers($key: ObjectId) {
        organisation(key: $key) {
            ...OrganisationAndStudyMemberFields
        }
    }
    ${OrganisationAndStudyMemberFragment}
`;

export const GET_ORGANISATION_MEMBERS_AND_ROLES = gql`
    query getOrganisationMembersAndRoles($key: ObjectId) {
        organisation(key: $key) {
            id
            name
            members {
                id
                roleId
            }
            roles {
                id
                created
                recordId
                active
                createdBy {
                    id
                    firstName
                    lastName
                }
                updated
                updatedBy {
                    id
                    firstName
                    lastName
                }
                name
                description
                modules
                rights {
                    moduleId
                    permissions
                    element
                    active
                }
            }
        }
    }
`;

export const GET_SYSTEM_PLUGIN = gql`
    query systemPlugin($timestamp: DateTime) {
        systemPlugin(timestamp: $timestamp) {
            ...OpraPluginFields
        }
    }
    ${OpraPluginFragment}
`;

export const SAVE_PLUGIN = gql`
    mutation savePlugin($studyId: ObjectId, $name: String!, $plugin: String, $reason: String, $system: Boolean) {
        savePlugin(studyId: $studyId, name: $name, plugin: $plugin, reason: $reason, system: $system) {
            ...OpraPluginFields
        }
    }
    ${OpraPluginFragment}
`;

export const INVITE_ORGANISATION_MEMBER = gql`
    mutation inviteOrganisationMember(
        $roleId: ObjectId!,
        $isAdmin: Boolean!,
        $isAuditAdmin: Boolean,
        $isReportAdmin: Boolean,
        $isObsAndLogsAdmin: Boolean,
        $isTrialOsAdmin: Boolean,
        $email: String!,
        $firstName: String,
        $lastName: String,
        $phone: String,
        $active: Boolean,
        $sendEmail: Boolean,
        $studyIds: [ObjectId!]!)
    {
        inviteOrganisationMember(
            roleId: $roleId,
            isAdmin: $isAdmin,
            isAuditAdmin: $isAuditAdmin,
            isReportAdmin: $isReportAdmin
            isObsAndLogsAdmin: $isObsAndLogsAdmin,
            isTrialOsAdmin: $isTrialOsAdmin,
            email: $email,
            firstName: $firstName,
            lastName: $lastName,
            phone: $phone,
            active: $active,
            sendEmail: $sendEmail,
            studyIds: $studyIds)
        {
            ...OrganisationFields
        }
    }
    ${OrganisationFragment}
`;

export const SAVE_ORGANISATION_MEMBER = gql`
    mutation saveOrganisationMember(
        $memberId: ObjectId!,
        $roleId: ObjectId!,
        $isAdmin: Boolean!,
        $isAuditAdmin: Boolean,
        $isReportAdmin: Boolean,
        $isObsAndLogsAdmin: Boolean,
        $isTrialOsAdmin: Boolean,
        $firstName: String!,
        $lastName: String!,
        $active: Boolean!,
        $email: String!,
        $phone: String!) 
    {
        saveOrganisationMember(
            memberId: $memberId,
            roleId: $roleId,
            isAdmin: $isAdmin,
            isAuditAdmin: $isAuditAdmin,
            isReportAdmin: $isReportAdmin,
            isObsAndLogsAdmin: $isObsAndLogsAdmin,
            isTrialOsAdmin: $isTrialOsAdmin,
            firstName: $firstName,
            lastName: $lastName,
            active: $active,
            email: $email,
            phone: $phone) 
        {
            ...OrganisationMemberFields
        }
    }
    ${OrganisationMemberFragment}
`;

export const SET_USER_SETTINGS = gql`
    mutation setSettings($settings: [KeyValueInput]!, $reason: String) {
        setSettings(settings: $settings, reason: $reason)
    }
`;

export const GET_ALL_USER_LIST = gql`
    query getUserList {
        users {
            id
            firstName
            lastName
            email
            systemAdminLevel
            isEnabled
        }
    }
`;

export const SET_ADMIN_ORGANISATION = gql`
    mutation selectTRIAdminOrganisation($organisationKey: ObjectId!) {
        selectTRIAdminOrganisation(organisationKey: $organisationKey) {
            ...OrganisationFields
        }
    }
    ${OrganisationFragment}
`;

export const UPDATE_USER_PASSWORD = gql`
    mutation updateUserPassword($currentPassword: String!, $newPassword: String!, $reason: String) {
        updateUserPassword(currentPassword: $currentPassword, newPassword: $newPassword, reason: $reason)
    }
`;

export const UPDATE_USER_EMAIL = gql`
    mutation updateUserEmail($currentPassword: String!, $newEmail: String!, $reason: String) {
        updateUserEmail(currentPassword: $currentPassword, newEmail: $newEmail, reason: $reason)
    }
`;

export const UPDATE_USER_PHONE = gql`
    mutation updateUserPhone($currentPassword: String!, $newPhone: String!, $reason: String) {
        updateUserPhone(currentPassword: $currentPassword, newPhone: $newPhone, reason: $reason)
    }
`;

export const UPDATE_USER_NAME = gql`
    mutation updateUserName($currentPassword: String!, $newFirstName: String!, $newLastName: String!, $reason: String) {
        updateUserName(currentPassword: $currentPassword, newFirstName: $newFirstName, newLastName: $newLastName, reason: $reason)
    }
`;

export const RESEND_INVITE = gql`
    mutation resendInvite($orgMemberId: ObjectId!) {
        resendInvite(orgMemberId: $orgMemberId)
    }
`;

// Gets all users for the current organisation, gets id and name only
export const GET_ORGANISATION_MEMBERS_ID_EMAIL = gql`
    query getOrganisationUsers($key: ObjectId) {
        organisation(key: $key) {
            id
            members {
                id
                userId
                email
            }
        }
    }
`;

// Gets all system users (system and org), gets id and name only
export const GET_ALL_USERS_ID_EMAIL_SYSADMINLEVEL = gql`
    query getAllUsersIdEmailSysAdminLevel {
        users {
            id
            email
            systemAdminLevel
        }
    }
`;

// Gets the full set of system users - only system users can use this query
export const GET_SYSTEM_USERS = gql`
    query systemUsers {
        systemUsers {
            ...SystemUserFields
        }
    }  
    ${SystemUserFragment}
`;

// Saves a system user - only system users can use this query
// Returns the user that was added/edited
export const SAVE_SYSTEM_USER = gql`
    mutation saveSystemUser(
        $id: ObjectId!,
        $active: Boolean,
        $systemAdminLevel: ConvertableInt!,
        $email: String!,
        $phone: String,
        $firstName: String!,
        $lastName: String!
    ) {
        saveSystemUser(
            id: $id,
            active: $active,
            systemAdminLevel: $systemAdminLevel,
            email: $email,
            phone: $phone,
            firstName: $firstName,
            lastName: $lastName
        ) {
            ...UserFields
        }
    }
    ${UserFragment}
`;

export const EXPORT_USERS = gql`
    query exportUsers($organisationKey: ObjectId) {
        exportUsers(organisationKey: $organisationKey)
    }  
`;
